import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Loader2, CheckCircle, RefreshCw,
    FileText, Target, Download, Play, Sparkles,
    TrendingUp, AlertTriangle, Award, LayoutGrid, List
} from 'lucide-react';
import BackNav from './components/BackNav';
import DomainScoreCard from './components/DomainScoreCard';
import SubdomainAccordion from './components/SubdomainAccordion';
import apiService from '../../../services/api';

// ─── Domain colour palette (matching DomainScoreCard) ─────────────────────────
const DOMAIN_COLORS = {
    '1.0': '#6366f1',
    '2.0': '#0ea5e9',
    '3.0': '#10b981',
    '4.0': '#8b5cf6',
    '5.0': '#f59e0b',
    '6.0': '#f43f5e',
    '7.0': '#14b8a6',
};

// ─── Build domain → subdomain → indicator hierarchy from flat indicators array ─
const buildHierarchy = (indicators, frameworkLookup) => {
    const domainMap = {};
    indicators.forEach(item => {
        const meta = frameworkLookup[item.indicator_id] || {};
        const enriched = {
            ...item,
            domain_id:      item.domain_id      || meta.domain_id      || '?',
            domain_name:    item.domain_name    || meta.domain_name    || item.domain_id || 'Unknown Domain',
            subdomain_id:   item.subdomain_id   || meta.subdomain_id   || '?',
            subdomain_name: item.subdomain_name || meta.subdomain_name || 'General',
            statement:      item.statement      || meta.statement      || item.indicator_id,
        };
        const { domain_id, domain_name, subdomain_id, subdomain_name } = enriched;

        if (!domainMap[domain_id]) {
            domainMap[domain_id] = { domain_id, domain_name, subdomains: {} };
        }
        if (!domainMap[domain_id].subdomains[subdomain_id]) {
            domainMap[domain_id].subdomains[subdomain_id] = { subdomain_id, subdomain_name, indicators: [] };
        }
        domainMap[domain_id].subdomains[subdomain_id].indicators.push(enriched);
    });

    // Sort: domains by id, subdomains by id
    return Object.values(domainMap)
        .sort((a, b) => a.domain_id.localeCompare(b.domain_id))
        .map(domain => ({
            ...domain,
            subdomains: Object.values(domain.subdomains).sort((a, b) => a.subdomain_id.localeCompare(b.subdomain_id)),
        }));
};


const SQAAFActionPlan = ({ audit, onBack }) => {
    const [loading, setLoading]               = useState(true);
    const [generating, setGenerating]         = useState(false);
    const [progress, setProgress]             = useState({ completed: 0, total: 84 });
    const [actionPlan, setActionPlan]         = useState(null);
    const [selectedDomain, setSelectedDomain] = useState('all');
    const [jobId, setJobId]                   = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [framework, setFramework]           = useState(null);
    const [viewMode, setViewMode]             = useState('hierarchy');   // 'hierarchy' | 'list'
    const reportRef = useRef(null);

    // Framework lookup: indicator_id → { domain_id, domain_name, subdomain_id, subdomain_name, statement }
    const frameworkLookup = useMemo(() => {
        if (!framework) return {};
        const map = {};
        (Array.isArray(framework) ? framework : framework?.domains || []).forEach(domain => {
            (domain.subdomains || []).forEach(sub => {
                (sub.indicators || []).forEach(ind => {
                    map[ind.indicator_id] = {
                        domain_id:      domain.domain_id,
                        domain_name:    domain.domain_name,
                        subdomain_id:   sub.subdomain_id,
                        subdomain_name: sub.subdomain_name,
                        statement:      ind.statement,
                    };
                });
            });
        });
        return map;
    }, [framework]);

    // Initial load
    useEffect(() => {
        checkExistingPlan();
        apiService.sqaaf.getFrameworkOverview()
            .then(data => setFramework(data?.domains || data || []))
            .catch(() => {});
    }, []);

    // Polling
    useEffect(() => {
        if (!jobId) return;
        const interval = setInterval(async () => {
            try {
                const status = await apiService.sqaaf.getActionPlanStatus(jobId);
                if (status.status === 'completed') {
                    setGenerating(false); setJobId(null); checkExistingPlan();
                } else if (status.status === 'failed') {
                    setGenerating(false); setJobId(null);
                    alert('Generation failed. Please try again.');
                } else {
                    setProgress(status.progress);
                }
            } catch (e) { console.error('Polling error', e); }
        }, 3000);
        return () => clearInterval(interval);
    }, [jobId]);

    const checkExistingPlan = async () => {
        try {
            setLoading(true);
            const result = await apiService.sqaaf.getFullActionPlan(audit.academic_year);
            if (result.success) setActionPlan(result.data);
        } catch (e) {
            console.log('No existing plan found');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmGeneration = async () => {
        setShowConfirmModal(false);
        if (!audit) { alert('Error: Audit data missing. Please refresh.'); return; }
        try {
            setGenerating(true);
            const res = await apiService.sqaaf.startActionPlanGeneration(audit.academic_year || '2025-2026');
            if (res.success) { setJobId(res.job_id); }
            else { alert('Failed to start: ' + (res.message || 'Unknown error')); setGenerating(false); }
        } catch (e) {
            console.error('Generation start failed', e);
            setGenerating(false);
            alert('Could not start generation. Please try again.');
        }
    };

    const handleDownloadPDF = () => {
        try {
            const allIndicators = actionPlan?.indicators || [];
            const enriched = allIndicators.map(item => {
                const meta = frameworkLookup[item.indicator_id] || {};
                return {
                    ...item,
                    domain_id:      item.domain_id      || meta.domain_id      || '?',
                    domain_name:    item.domain_name    || meta.domain_name    || 'Unknown Domain',
                    subdomain_name: item.subdomain_name || meta.subdomain_name || 'General',
                    statement:      item.statement      || meta.statement      || item.indicator_id,
                };
            });

            // Group by domain → subdomain
            const domainMap = {};
            enriched.forEach(item => {
                const dk = item.domain_id;
                if (!domainMap[dk]) domainMap[dk] = { name: item.domain_name, subdomains: {} };
                const sk = item.subdomain_name;
                if (!domainMap[dk].subdomains[sk]) domainMap[dk].subdomains[sk] = [];
                domainMap[dk].subdomains[sk].push(item);
            });

            const priorityClr = p => p === 'High' ? '#d95353' : p === 'Medium' ? '#f47c20' : '#2d81c1';
            const dateStr = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
            const totalAct = enriched.reduce((s, i) => s + (i.plan?.recommendations?.length || 0), 0);

            let domainsHtml = '';
            Object.entries(domainMap).sort((a, b) => a[0].localeCompare(b[0])).forEach(([, domain]) => {
                let subHtml = '';
                Object.entries(domain.subdomains).forEach(([subName, indicators]) => {
                    let indicatorsHtml = '';
                    indicators.forEach(ind => {
                        const recs = ind.plan?.recommendations || [];
                        if (!recs.length) return;
                        const recsHtml = recs.map(r => `
                            <tr style="page-break-inside:avoid;">
                                <td style="padding:7px 10px;border-bottom:1px solid #f0f4f8;font-size:8.5pt;color:#233445;line-height:1.6;">${r.action || r.text || ''}</td>
                                <td style="padding:7px 10px;border-bottom:1px solid #f0f4f8;font-size:8.5pt;white-space:nowrap;">
                                    <span style="font-weight:700;color:${priorityClr(r.priority)};">${r.priority || '—'}</span>
                                </td>
                                <td style="padding:7px 10px;border-bottom:1px solid #f0f4f8;font-size:8.5pt;color:#617587;white-space:nowrap;">${r.timeline || '—'}</td>
                                <td style="padding:7px 10px;border-bottom:1px solid #f0f4f8;font-size:8.5pt;color:#617587;">${r.responsible || r.owner || '—'}</td>
                            </tr>`).join('');
                        indicatorsHtml += `
                            <div style="margin-bottom:12px;page-break-inside:avoid;">
                                <div style="font-size:8.5pt;font-weight:600;color:#617587;margin-bottom:6px;line-height:1.5;">
                                    <span style="background:#f5f7fb;border:1px solid #e6ecf3;border-radius:4px;padding:2px 7px;font-size:7.5pt;margin-right:6px;">${ind.indicator_id}</span>
                                    ${ind.statement}
                                </div>
                                <table style="width:100%;border-collapse:collapse;font-size:8.5pt;">
                                    <thead><tr style="background:#fafafa;">
                                        <th style="text-align:left;padding:6px 10px;font-size:7pt;color:#8ea0b1;font-weight:600;text-transform:uppercase;border-bottom:1px solid #e6ecf3;width:52%;">Action</th>
                                        <th style="text-align:left;padding:6px 10px;font-size:7pt;color:#8ea0b1;font-weight:600;text-transform:uppercase;border-bottom:1px solid #e6ecf3;width:12%;">Priority</th>
                                        <th style="text-align:left;padding:6px 10px;font-size:7pt;color:#8ea0b1;font-weight:600;text-transform:uppercase;border-bottom:1px solid #e6ecf3;width:16%;">Timeline</th>
                                        <th style="text-align:left;padding:6px 10px;font-size:7pt;color:#8ea0b1;font-weight:600;text-transform:uppercase;border-bottom:1px solid #e6ecf3;width:20%;">Responsible</th>
                                    </tr></thead>
                                    <tbody>${recsHtml}</tbody>
                                </table>
                            </div>`;
                    });
                    if (!indicatorsHtml) return;
                    subHtml += `
                        <div style="margin-bottom:16px;">
                            <div style="font-size:9pt;font-weight:700;color:#1a2e44;background:#f5f7fb;border:1px solid #e6ecf3;border-radius:6px;padding:7px 12px;margin-bottom:10px;page-break-after:avoid;">${subName}</div>
                            ${indicatorsHtml}
                        </div>`;
                });
                if (!subHtml) return;
                domainsHtml += `
                    <div style="margin-bottom:22px;">
                        <div style="font-size:11pt;font-weight:800;color:#1a2e44;border-bottom:2px solid #2d81c1;padding-bottom:6px;margin-bottom:14px;page-break-after:avoid;">${domain.name}</div>
                        ${subHtml}
                    </div>`;
            });

            const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>School Improvement Plan — ${audit?.school_name || 'School'}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 10pt; color: #233445; background: #fff; line-height: 1.6;
         -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  @page { size: A4 portrait; margin: 14mm 15mm 14mm 15mm; }
  .header { border-bottom: 3px solid #D67C2C; padding-bottom: 12px; margin-bottom: 14px; page-break-inside: avoid; }
  .stats { display: grid; grid-template-columns: repeat(4,1fr); gap: 8px; margin-bottom: 14px; page-break-inside: avoid; }
  .stat { background: #f5f7fb; border: 1px solid #e6ecf3; border-radius: 8px; padding: 10px 8px; text-align: center; }
  .stat-lbl { font-size: 6.5pt; color: #8ea0b1; font-weight: 600; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 4px; }
  .stat-val { font-size: 16pt; font-weight: 800; color: #D67C2C; }
  .stat-sub { font-size: 6.5pt; color: #8ea0b1; margin-top: 3px; }
  .footer { margin-top: 16px; padding-top: 8px; border-top: 1px solid #e6ecf3; display: flex; justify-content: space-between; font-size: 7.5pt; color: #8ea0b1; }
</style>
</head>
<body>

<div class="header">
  <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:12px;">
    <div>
      <div style="font-size:7.5pt;font-weight:700;color:#D67C2C;text-transform:uppercase;letter-spacing:1px;margin-bottom:3px;">Holistic Evaluator · SQAAF</div>
      <div style="font-size:18pt;font-weight:800;color:#1a2e44;line-height:1.2;">School Improvement Plan</div>
      <div style="font-size:8.5pt;color:#617587;margin-top:3px;">AI-generated indicator-level improvement roadmap</div>
    </div>
    <div style="text-align:right;flex-shrink:0;">
      <div style="font-size:8pt;color:#617587;">Academic Year</div>
      <div style="font-size:10pt;font-weight:700;color:#1a2e44;">${audit?.academic_year || '—'}</div>
      <div style="font-size:7.5pt;color:#8ea0b1;margin-top:4px;">Generated: ${dateStr}</div>
    </div>
  </div>
</div>

<div class="stats">
  <div class="stat"><div class="stat-lbl">Total Actions</div><div class="stat-val">${totalAct}</div><div class="stat-sub">improvement actions</div></div>
  <div class="stat"><div class="stat-lbl">Indicators Covered</div><div class="stat-val">${enriched.filter(i => i.plan).length}</div><div class="stat-sub">with action plans</div></div>
  <div class="stat"><div class="stat-lbl">Domains</div><div class="stat-val">${Object.keys(domainMap).length}</div><div class="stat-sub">areas covered</div></div>
  <div class="stat"><div class="stat-lbl">84 Indicators</div><div class="stat-val" style="font-size:10pt;color:#2d9b68;padding-top:4px;">Analysed</div><div class="stat-sub">SQAAF framework</div></div>
</div>

${domainsHtml}

<div class="footer">
  <span>${audit?.school_name || 'School'} · School Improvement Plan · ${audit?.academic_year || ''}</span>
  <span>Generated via Holistic Evaluator · ${dateStr}</span>
</div>

</body>
</html>`;

            const win = window.open('', '_blank', 'width=900,height=720');
            if (!win) { alert('Allow pop-ups to export the PDF.'); return; }
            win.document.write(html);
            win.document.close();
            win.onload = () => { win.focus(); win.print(); };
        } catch (e) {
            console.error('Export failed', e);
            alert('Failed to export PDF: ' + (e?.message || 'Unknown error'));
        }
    };

    // ── Enrich + filter indicators ─────────────────────────────────────────────
    const enrichedIndicators = useMemo(() => (actionPlan?.indicators || []).map(item => {
        const meta = frameworkLookup[item.indicator_id] || {};
        return {
            ...item,
            domain_id:      item.domain_id      || meta.domain_id      || '?',
            domain_name:    item.domain_name    || meta.domain_name    || 'Unknown',
            subdomain_id:   item.subdomain_id   || meta.subdomain_id   || '?',
            subdomain_name: item.subdomain_name || meta.subdomain_name || 'General',
            statement:      item.statement      || meta.statement      || item.indicator_id,
        };
    }), [actionPlan, frameworkLookup]);

    const filteredIndicators = useMemo(() =>
        selectedDomain === 'all'
            ? enrichedIndicators
            : enrichedIndicators.filter(i => i.domain_id === selectedDomain),
    [enrichedIndicators, selectedDomain]);

    // Build hierarchy for display
    const hierarchy = useMemo(() => buildHierarchy(filteredIndicators, frameworkLookup), [filteredIndicators, frameworkLookup]);

    // Unique domains list
    const domainList = useMemo(() => {
        const seen = {};
        enrichedIndicators.forEach(i => { if (i.domain_id && !seen[i.domain_id]) seen[i.domain_id] = i.domain_name; });
        return Object.entries(seen).map(([id, name]) => ({ id, name })).sort((a, b) => a.id.localeCompare(b.id));
    }, [enrichedIndicators]);

    // Domain scores computed live from audit.responses × framework (audit.domain_scores doesn't exist on the API response)
    const domainScores = useMemo(() => {
        const map = {};
        if (!audit?.responses || !framework) return map;
        const domains = Array.isArray(framework) ? framework : (framework?.domains || []);
        domains.forEach(domain => {
            const domainIndicators = (domain.subdomains || []).flatMap(s => s.indicators || []);
            const totalIndicators = domainIndicators.length;
            if (!totalIndicators) return;
            const domainResponses = (audit.responses || []).filter(r =>
                domainIndicators.some(i => i.indicator_id === r.indicator_id)
            );
            const totalScore = domainResponses.reduce((sum, r) => sum + (Number(r.selected_level) || 0), 0);
            const averageLevel = totalScore / totalIndicators;
            map[domain.domain_id] = {
                average_level: parseFloat(averageLevel.toFixed(2)),
                score_percent: Math.round((totalScore / (totalIndicators * 4)) * 100),
            };
        });
        return map;
    }, [audit, framework]);

    // Action count per domain
    const actionCountByDomain = useMemo(() => {
        const counts = {};
        enrichedIndicators.forEach(item => {
            const d = item.domain_id;
            if (!counts[d]) counts[d] = 0;
            counts[d] += (item.plan?.recommendations?.length || 0);
        });
        return counts;
    }, [enrichedIndicators]);

    // Overall stats
    const totalActions = useMemo(() => enrichedIndicators.reduce((s, i) => s + (i.plan?.recommendations?.length || 0), 0), [enrichedIndicators]);
    const readyCount   = enrichedIndicators.filter(i => !i.error && i.plan).length;
    const errorCount   = enrichedIndicators.filter(i => i.error).length;

    // ── LOADING ────────────────────────────────────────────────────────────────
    if (loading) return (
        <div className="flex items-center justify-center min-h-[60vh]">
            <Loader2 className="w-8 h-8 animate-spin text-[#D67C2C]" />
        </div>
    );

    // ── GENERATING ────────────────────────────────────────────────────────────
    if (generating || (jobId && !actionPlan)) {
        const pct = Math.round((progress.completed / Math.max(1, progress.total)) * 100);
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 font-[Poppins]">
                <div className="w-28 h-28 mb-6 relative flex items-center justify-center">
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="44" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                        <circle cx="50" cy="50" r="44" fill="none" stroke="#D67C2C" strokeWidth="8"
                            strokeLinecap="round"
                            strokeDasharray="276"
                            strokeDashoffset={276 - (276 * pct / 100)}
                            className="transition-all duration-500"
                        />
                    </svg>
                    <span className="absolute text-2xl font-bold text-[#D67C2C]">{pct}%</span>
                </div>
                <h2 className="text-2xl font-bold text-[#1D3469] mb-2">Building Your Action Plan</h2>
                <p className="text-gray-500 mb-6 max-w-md">
                    Analysing {progress.total} indicators to craft personalised improvement strategies for your school…
                </p>
                <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 border border-green-200 px-4 py-2 rounded-full">
                    <CheckCircle className="w-4 h-4" />
                    {progress.completed} of {progress.total} analysed
                </div>
                <p className="mt-6 text-xs text-gray-400">You can safely close this window. We'll save your plan automatically.</p>
            </div>
        );
    }

    // ── NO PLAN ───────────────────────────────────────────────────────────────
    if (!actionPlan) {
        return (
            <div className="font-[Poppins] flex flex-col items-center justify-center min-h-[60vh] text-center p-8">
                <div className="w-20 h-20 bg-orange-50 rounded-2xl flex items-center justify-center mb-6">
                    <Target className="w-10 h-10 text-[#D67C2C]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-3">Generate Your School Improvement Plan</h2>
                <p className="text-gray-500 mb-8 max-w-lg leading-relaxed">
                    Get a comprehensive, indicator-by-indicator roadmap with prioritised actions, timelines, responsible roles, and success KPIs — tailored to your school's assessment results.
                </p>
                <button
                    onClick={() => setShowConfirmModal(true)}
                    className="px-8 py-4 bg-[#D67C2C] text-white rounded-xl text-base font-bold hover:bg-[#c06a25] transition-colors flex items-center gap-3 shadow-lg hover:shadow-xl cursor-pointer"
                >
                    <Play className="w-5 h-5 fill-current" />
                    Generate Action Plan
                </button>
                {showConfirmModal && <ConfirmModal onConfirm={handleConfirmGeneration} onCancel={() => setShowConfirmModal(false)} />}
            </div>
        );
    }

    // ── FULL PLAN VIEW ────────────────────────────────────────────────────────
    return (
        <div className="w-full pb-20 font-[Poppins]">
            <BackNav
                onBack={onBack}
                right={
                    <div className="flex gap-2">
                        <button onClick={handleDownloadPDF} className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1.5 cursor-pointer transition-colors">
                            <Download className="w-4 h-4" /> Export PDF
                        </button>
                        <button onClick={() => setShowConfirmModal(true)} className="px-3 py-2 bg-orange-50 text-[#D67C2C] rounded-lg text-sm hover:bg-orange-100 flex items-center gap-1.5 cursor-pointer transition-colors font-medium">
                            <RefreshCw className="w-4 h-4" /> Regenerate
                        </button>
                    </div>
                }
            />

            <div ref={reportRef} className="px-4 sm:px-6 pt-6">

                {/* ── Header + Overall Stats ──────────────────────────────── */}
                <div className="mb-6">
                    <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
                        <div>
                            <h1 className="text-2xl font-bold text-[#1D3469] flex items-center gap-2">
                                <FileText className="w-6 h-6 text-[#D67C2C]" />
                                School Improvement Plan
                            </h1>
                            <p className="text-gray-500 text-sm mt-1">
                                Generated {new Date(actionPlan.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} &bull; {enrichedIndicators.length} indicators analysed
                            </p>
                        </div>
                        {/* View mode toggle */}
                        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('hierarchy')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${viewMode === 'hierarchy' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                <LayoutGrid className="w-3.5 h-3.5" /> Hierarchy
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${viewMode === 'list' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                <List className="w-3.5 h-3.5" /> List
                            </button>
                        </div>
                    </div>

                    {/* Summary stats row */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                            { icon: Target,        label: 'Total Actions',    value: totalActions,   color: 'text-[#D67C2C]', bg: 'bg-orange-50' },
                            { icon: CheckCircle,   label: 'Indicators Ready', value: readyCount,     color: 'text-green-600', bg: 'bg-green-50'  },
                            { icon: AlertTriangle, label: 'Failed to Generate', value: errorCount,   color: 'text-red-500',   bg: 'bg-red-50'    },
                            { icon: Award,         label: 'Domains Covered',  value: domainList.length, color: 'text-indigo-600', bg: 'bg-indigo-50' },
                        ].map(({ icon: Icon, label, value, color, bg }) => (
                            <div key={label} className={`${bg} rounded-xl p-4 flex items-center gap-3`}>
                                <Icon className={`w-5 h-5 ${color} flex-shrink-0`} />
                                <div>
                                    <p className="text-lg font-bold text-gray-800 leading-none">{value}</p>
                                    <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Domain Score Cards ──────────────────────────────────── */}
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Domain Overview</p>
                        <button
                            onClick={() => setSelectedDomain('all')}
                            className={`text-xs font-medium transition-colors cursor-pointer px-3 py-1 rounded-full ${selectedDomain === 'all' ? 'bg-[#D67C2C] text-white' : 'text-gray-500 hover:text-[#D67C2C]'}`}
                        >
                            All Domains
                        </button>
                    </div>
                    <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
                        {domainList.map(d => {
                            const ds = domainScores[d.id] || {};
                            const scorePercent = ds.score_percent ?? 0;
                            return (
                                <DomainScoreCard
                                    key={d.id}
                                    domainId={d.id}
                                    domainName={d.name}
                                    score={scorePercent}
                                    averageLevel={ds.average_level || 0}
                                    actionCount={actionCountByDomain[d.id] || 0}
                                    isActive={selectedDomain === d.id}
                                    onClick={() => setSelectedDomain(prev => prev === d.id ? 'all' : d.id)}
                                />
                            );
                        })}
                    </div>
                </div>

                {/* ── Content Area ─────────────────────────────────────────── */}
                {filteredIndicators.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        <TrendingUp className="w-10 h-10 mx-auto mb-3 text-gray-200" />
                        <p className="text-sm">No action items for this domain.</p>
                    </div>
                ) : viewMode === 'hierarchy' ? (
                    /* ── Hierarchy View ────────────────────────────────────── */
                    <div className="space-y-6">
                        {hierarchy.map(domain => (
                            <div key={domain.domain_id}>
                                {/* Domain header band */}
                                <div className="flex items-center gap-3 mb-3">
                                    <div
                                        className="w-3 h-3 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: DOMAIN_COLORS[domain.domain_id] || '#D67C2C' }}
                                    />
                                    <h2 className="text-base font-bold text-[#1D3469]">{domain.domain_name}</h2>
                                    <div className="flex-1 h-px bg-gray-100" />
                                    <span className="text-xs text-gray-400">
                                        {domain.subdomains.length} subdomain{domain.subdomains.length !== 1 ? 's' : ''}
                                    </span>
                                </div>

                                {/* Subdomains */}
                                <div className="space-y-2 pl-6">
                                    {domain.subdomains.map(sub => (
                                        <SubdomainAccordion
                                            key={sub.subdomain_id}
                                            subdomainName={sub.subdomain_name}
                                            subdomainId={sub.subdomain_id}
                                            indicators={sub.indicators}
                                            defaultOpen={sub.indicators.some(i => (i.average_level || i.current_level || 2) <= 2)}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    /* ── List View (original style, improved) ──────────────── */
                    <div className="space-y-3">
                        {filteredIndicators.map((item, idx) => (
                            <FlatIndicatorCard key={idx} item={item} />
                        ))}
                    </div>
                )}
            </div>

            {showConfirmModal && <ConfirmModal onConfirm={handleConfirmGeneration} onCancel={() => setShowConfirmModal(false)} />}
        </div>
    );
};


// ─── Flat list card (list view mode) ──────────────────────────────────────────
const FlatIndicatorCard = ({ item }) => {
    const [open, setOpen] = useState(false);
    const recs = item.plan?.recommendations || [];

    return (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white hover:shadow-md transition-shadow">
            <button
                onClick={() => setOpen(p => !p)}
                className="w-full p-4 flex items-start gap-3 text-left hover:bg-gray-50 cursor-pointer"
            >
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-[#D67C2C] bg-orange-50 px-2 py-0.5 rounded">{item.indicator_id}</span>
                        <span className="text-xs text-gray-400 uppercase tracking-wide font-semibold">{item.domain_name}</span>
                    </div>
                    <p className="text-sm text-gray-800 font-medium leading-snug">{item.statement}</p>
                </div>
                {item.error ? (
                    <span className="text-xs text-red-500 flex items-center gap-1 flex-shrink-0">
                        <AlertTriangle className="w-3.5 h-3.5" /> Failed
                    </span>
                ) : (
                    <span className="text-xs text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0">
                        <CheckCircle className="w-3 h-3" /> {recs.length} action{recs.length !== 1 ? 's' : ''}
                    </span>
                )}
            </button>

            {open && !item.error && item.plan && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-2">
                    {recs.map((rec, i) => (
                        <div key={i} className="bg-gray-50 rounded-lg p-3">
                            <p className="text-sm text-gray-800 font-medium mb-2">{rec.action}</p>
                            <div className="flex flex-wrap gap-2 text-xs">
                                {rec.timeline && <span className="bg-white border border-gray-200 px-2 py-0.5 rounded text-gray-500">⏱ {rec.timeline}</span>}
                                {rec.responsibility && <span className="bg-white border border-gray-200 px-2 py-0.5 rounded text-gray-500">👤 {rec.responsibility}</span>}
                            </div>
                        </div>
                    ))}
                    {item.plan.kpi && (
                        <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs">
                            <Target className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <div><span className="font-semibold text-emerald-700">KPI: </span><span className="text-emerald-700">{item.plan.kpi}</span></div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};


// ─── Confirm modal ─────────────────────────────────────────────────────────────
const ConfirmModal = ({ onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl font-[Poppins]">
            <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mb-4">
                    <Sparkles className="w-8 h-8 text-[#D67C2C]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Generate Action Plan?</h3>
                <p className="text-gray-500 mb-6 leading-relaxed">
                    This will analyse your responses across <strong>80+ indicators</strong> to create a personalised improvement roadmap.
                    <br /><br />
                    <span className="text-sm bg-orange-50 text-[#D67C2C] px-3 py-1 rounded-full">Estimated time: 3–5 minutes</span>
                </p>
                <div className="flex gap-3 w-full">
                    <button onClick={onCancel} className="flex-1 py-3 px-4 border border-gray-200 rounded-xl text-gray-600 font-medium hover:bg-gray-50 transition-colors cursor-pointer">
                        Cancel
                    </button>
                    <button onClick={onConfirm} className="flex-1 py-3 px-4 bg-[#D67C2C] text-white rounded-xl font-bold hover:bg-[#c06a25] transition-colors shadow-lg cursor-pointer flex items-center justify-center gap-2">
                        <Play className="w-4 h-4 fill-current" /> Start Analysis
                    </button>
                </div>
                <p className="text-xs text-gray-400 mt-4">You can navigate away while we generate your plan.</p>
            </div>
        </div>
    </div>
);

export default SQAAFActionPlan;
